/**
 * SectionGrid — Overview grid wrapping ModelSectionGrid.
 * Extracted from FinancialModelEditor.tsx lines ~2260–2299 (Phase 3A).
 */

import React from 'react';
import { Box } from '@mui/material';
import TutorialStep from '../../../components/Tutorial';
import ModelSectionGrid from '../../../components/ModelSectionGrid';
import { ModelSection } from '../../../types/ModelSection';

interface SectionGridProps {
    sections: ModelSection[];
    sectionsLoading: boolean;
    searchQuery: string;
    onSectionClick: (section: ModelSection) => void;
}

export default function SectionGrid({
    sections,
    sectionsLoading,
    searchQuery,
    onSectionClick,
}: SectionGridProps) {
    if (sectionsLoading) {
        return (
            <Box sx={{ padding: 4 }}>
                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                    {[...Array(8)].map((_, i) => (
                        <div key={i} className="animate-pulse">
                            <div className="h-48 bg-slate-200 rounded-lg"></div>
                        </div>
                    ))}
                </div>
            </Box>
        );
    }

    if (sections.length === 0) {
        return (
            <Box sx={{ padding: 4 }}>
                <div className="text-center py-16">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-slate-100 mb-6">
                        <span className="material-symbols-outlined text-slate-400 text-[48px]">
                            account_tree
                        </span>
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">
                        No sections available
                    </h3>
                    <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto">
                        Select a scenario to view and manage your financial model sections.
                        Each section organizes related inputs and outputs for better navigation.
                    </p>
                    <p className="text-xs text-slate-400">
                        Tip: Use the Scenario selector above to load a scenario
                    </p>
                </div>
            </Box>
        );
    }

    return (
        <Box sx={{ padding: 4 }}>
            <TutorialStep tutorial='main' identifier='architecture_overview' display='block' fixedCenter>
                <ModelSectionGrid
                    sections={sections}
                    onSectionClick={onSectionClick}
                    searchQuery={searchQuery}
                />
            </TutorialStep>
        </Box>
    );
}
